import type {DiffLine, Thread, ThreadsData} from '@github-ui/diff-lines'
import type {DiffEntry, DiffSide} from '@github-ui/diff-lines/types'
import type {PullRequestFileTreeDiff} from '../page-data/payloads/file-tree'
import type {Markers} from '../stores/slices/markers'
import type {DiffAnnotation, Thread as ConversationThread} from '@github-ui/conversations'

type AnnotationsData = {
  totalCount: number
  annotations: Array<DiffAnnotation | null> | null
}

export function findMarkersByPosition(
  diffLine: DiffLine,
  diffEntryData: PullRequestFileTreeDiff,
  markersData: Markers,
): {threadsData: ThreadsData; annotationsData: AnnotationsData} | undefined {
  const diffLineKey = diffLine.type !== 'DELETION' ? `R${diffLine.right}` : `L${diffLine.left}`

  if (!diffEntryData || !diffEntryData.markersMap) {
    return undefined
  }

  const markerPositions = diffEntryData.markersMap[diffLineKey]
  const threads: Thread[] = []
  const annotations: DiffAnnotation[] = []
  let totalCommentsCount = 0

  if (markerPositions) {
    for (const marker of markerPositions.threads) {
      if (marker.id !== undefined) {
        const thread = markersData.threads[Number(marker.id)]
        if (thread) {
          let startSide: DiffSide | undefined = undefined
          let startLineNumber = undefined
          if (marker.start) {
            startSide = marker.start[0] === 'L' ? 'LEFT' : 'RIGHT'
            startLineNumber = parseInt(marker.start.slice(1)) || -1
          }

          totalCommentsCount += thread.commentsData.comments.length
          threads.push({
            id: thread.id,
            isOutdated: thread.isOutdated || false,
            commentsData: {
              __id: thread.commentsData.__id,
              comments: thread.commentsData.comments.map(comment => ({
                id: comment.databaseId ?? undefined,
                author: comment.author ?? null,
              })),
              totalCount: thread.commentsData.comments.length,
            },
            diffSide: diffLine.type === 'DELETION' ? 'LEFT' : 'RIGHT',
            line: diffLine.type === 'DELETION' ? diffLine.left : diffLine.right,
            startDiffSide: startSide,
            startLine: startLineNumber,
          })
        }
      }
    }

    for (const marker of markerPositions.annotations) {
      if (marker.id !== undefined) {
        const annotation = markersData.annotations[Number(marker.id)]
        if (annotation) {
          annotations.push(annotation)
        }
      }
    }
  }

  return {
    threadsData: {
      threads,
      totalCommentsCount,
      totalCount: threads.length,
    },
    annotationsData: {
      totalCount: annotations.length,
      annotations,
    },
  }
}

export function extractAnnotationMarkerId(
  parsedAnnotationId: number,
  annotations: Record<number, Pick<DiffAnnotation, 'id' | 'databaseId'>> | undefined,
) {
  if (!parsedAnnotationId || !annotations) {
    return undefined
  }

  for (const annotation of Object.values(annotations)) {
    if (annotation && annotation.databaseId === parsedAnnotationId) {
      return annotation.id
    }
  }

  return undefined
}

// Narrowed type for threads to match the expected structure in extractCommentThreadMarkerId
// This simplifies the type checking for tests and ensures we only work with the necessary properties
type ThreadForHashFunctions = {
  id: ConversationThread['id']
  commentsData: {
    comments: Array<Pick<ConversationThread['commentsData']['comments'][number], 'databaseId'>>
  }
}

export function extractCommentThreadMarkerId(
  parsedCommentId: number | undefined,
  threads: Record<number, ThreadForHashFunctions> | undefined,
) {
  if (!parsedCommentId || !threads) {
    return undefined
  }

  for (const thread of Object.values(threads)) {
    if (thread) {
      for (const comment of thread.commentsData.comments) {
        if (comment.databaseId === parsedCommentId) {
          return thread.id
        }
      }
    }
  }

  return undefined
}

export function buildCollapsedDiffStatusMap(diffSummaries: PullRequestFileTreeDiff[]): Record<string, boolean> {
  return diffSummaries.reduce<Record<string, boolean>>((buildingCollapsedDiffStatusMap, diffSummary) => {
    buildingCollapsedDiffStatusMap[diffSummary.path] = !!diffSummary.markedAsViewed
    return buildingCollapsedDiffStatusMap
  }, {})
}

export function buildDiffEntryMap(diffEntries: DiffEntry[]): Record<string, DiffEntry> {
  return diffEntries.reduce<Record<string, DiffEntry>>((buildingDiffEntryMap, diffEntry) => {
    buildingDiffEntryMap[diffEntry.path] = diffEntry
    return buildingDiffEntryMap
  }, {})
}
